import type { EventPhase } from '@/lib/trackingEngine';

export type WorkflowStep = {
  day: number;
  status: string;
  info: string;
};

export type WorkflowTemplate = {
  initial_shipment: {
    processing: WorkflowStep[];
    in_transit: WorkflowStep[];
    out_for_delivery: WorkflowStep[];
  };
  return_and_replacement: WorkflowStep[];
  replacement_shipment: {
    processing: WorkflowStep[];
    in_transit: WorkflowStep[];
    out_for_delivery: WorkflowStep[];
  };
};

export type WorkflowFlatStep = WorkflowStep & {
  phase: EventPhase;
  isDamageEvent?: boolean;
  isReplacementEvent?: boolean;
};

export const DEFAULT_WORKFLOW_TEMPLATE: WorkflowTemplate = {
  initial_shipment: {
    processing: [
      { day: 0, status: 'Shipment label created', info: 'The shipment information has been received and the shipment label has been generated.' },
      { day: 0, status: 'Pickup scheduled', info: 'Pickup has been scheduled with the store warehouse for collection of the shipment.' },
      { day: 1, status: 'Shipment collected', info: 'The shipment has been successfully collected from the store warehouse and is now in our custody.' },
      { day: 2, status: 'Arrived at shipping company warehouse', info: 'The shipment has arrived at our warehouse for verification and handling.' },
      { day: 3, status: 'Shipment under processing', info: 'The shipment is being verified, secured, and prepared for transportation.' },
      { day: 4, status: 'Shipment prepared for dispatch', info: 'The shipment has completed processing and is ready for dispatch.' },
    ],
    in_transit: [
      { day: 5, status: 'Shipment dispatched', info: 'The shipment has departed from our warehouse and is moving through the transportation network.' },
      { day: 7, status: 'In transit', info: 'The shipment is moving through our transportation network.' },
      { day: 9, status: 'Arrived at transit facility', info: 'The shipment has arrived at an intermediate facility for routing.' },
      { day: 10, status: 'Departed transit facility', info: 'The shipment has departed the transit facility and continues toward destination.' },
      { day: 13, status: 'Arrived at destination facility', info: 'The shipment has reached the destination area facility for final processing.' },
      { day: 14, status: 'Shipment processed at destination facility', info: 'The shipment is being prepared for final delivery routing.' },
    ],
    out_for_delivery: [
      { day: 16, status: 'Transferred to local delivery facility', info: 'The shipment has been transferred to the local facility responsible for final delivery.' },
      { day: 17, status: 'Under final handling', info: 'The shipment is undergoing final operational handling before delivery.' },
      { day: 18, status: 'Out for delivery', info: 'The shipment is currently with the delivery team and on the way to the destination address.' },
      { day: 20, status: 'Delivery in progress', info: 'The shipment is in the final stage of transportation.' },
      { day: 21, status: 'Shipment damaged during transit', info: 'The shipment was damaged during transportation and has been returned to our warehouse for further handling.' },
    ],
  },
  return_and_replacement: [
    { day: 22, status: 'Return to store initiated', info: 'The damaged shipment is being returned to the store warehouse for inspection and replacement processing.' },
    { day: 23, status: 'Returned to store warehouse', info: 'The damaged shipment has been delivered back to the store warehouse.' },
    { day: 24, status: 'Replacement review in progress', info: 'The store is inspecting the returned shipment and reviewing the replacement request.' },
    { day: 25, status: 'Replacement approved', info: 'The store has approved the replacement and has started preparation of the new shipment.' },
    { day: 26, status: 'Replacement item under preparation', info: 'The replacement item is being prepared by the store warehouse.' },
    { day: 27, status: 'Replacement item being finalized', info: 'The replacement shipment is undergoing final preparation and packaging.' },
    { day: 28, status: 'Replacement ready for release', info: 'The replacement shipment is ready for collection and a new shipping cycle will begin.' },
  ],
  replacement_shipment: {
    processing: [
      { day: 29, status: 'Shipment label created', info: 'A new shipment label has been generated for the replacement shipment.' },
      { day: 29, status: 'Pickup scheduled', info: 'Pickup has been scheduled with the store warehouse for collection of the replacement shipment.' },
      { day: 30, status: 'Replacement shipment collected', info: 'The replacement shipment has been successfully collected from the store warehouse and is now in our custody.' },
      { day: 31, status: 'Arrived at shipping company warehouse', info: 'The replacement shipment has arrived at our warehouse for verification and handling.' },
      { day: 32, status: 'Replacement shipment under processing', info: 'The replacement shipment is being verified, secured, and prepared for transportation.' },
      { day: 33, status: 'Replacement shipment prepared for dispatch', info: 'The replacement shipment has completed processing and is ready for dispatch.' },
    ],
    in_transit: [
      { day: 34, status: 'Replacement shipment dispatched', info: 'The replacement shipment has departed from our warehouse and is moving through the transportation network.' },
      { day: 36, status: 'In transit', info: 'The replacement shipment is moving through our transportation network.' },
      { day: 38, status: 'Arrived at transit facility', info: 'The replacement shipment has arrived at an intermediate facility for routing.' },
      { day: 39, status: 'Departed transit facility', info: 'The replacement shipment has departed the transit facility and continues toward destination.' },
      { day: 42, status: 'Arrived at destination facility', info: 'The replacement shipment has reached the destination area facility for final processing.' },
      { day: 43, status: 'Replacement shipment processed at destination facility', info: 'The replacement shipment is being prepared for final delivery routing.' },
    ],
    out_for_delivery: [
      { day: 45, status: 'Transferred to local delivery facility', info: 'The replacement shipment has been transferred to the local facility responsible for final delivery.' },
      { day: 46, status: 'Awaiting customer availability', info: 'The replacement shipment is ready for final delivery and is pending customer availability.' },
      { day: 47, status: 'Out for delivery', info: 'The replacement shipment is currently with the delivery team and on the way to the destination address.' },
      { day: 49, status: 'Delivered', info: 'The replacement shipment has been successfully delivered.' },
    ],
  },
};

export function flattenWorkflowTemplate(template: WorkflowTemplate): WorkflowFlatStep[] {
  const flat: WorkflowFlatStep[] = [];

  for (const step of template.initial_shipment.processing) {
    flat.push({ ...step, phase: 'initial' });
  }
  for (const step of template.initial_shipment.in_transit) {
    flat.push({ ...step, phase: 'in_transit' });
  }
  for (const step of template.initial_shipment.out_for_delivery) {
    const lower = step.status.toLowerCase();
    if (lower.includes('damaged')) {
      flat.push({ ...step, phase: 'damaged', isDamageEvent: true });
    } else {
      flat.push({ ...step, phase: 'out_for_delivery' });
    }
  }

  template.return_and_replacement.forEach((step, idx) => {
    if (idx <= 1) {
      flat.push({ ...step, phase: 'return' });
    } else {
      flat.push({ ...step, phase: 'replacement_review', isReplacementEvent: true });
    }
  });

  for (const step of template.replacement_shipment.processing) {
    flat.push({ ...step, phase: 'replacement_transit', isReplacementEvent: true });
  }
  for (const step of template.replacement_shipment.in_transit) {
    flat.push({ ...step, phase: 'replacement_transit', isReplacementEvent: true });
  }
  for (const step of template.replacement_shipment.out_for_delivery) {
    if (step.status.toLowerCase() === 'delivered') {
      flat.push({ ...step, phase: 'delivered', isReplacementEvent: true });
    } else {
      flat.push({ ...step, phase: 'replacement_transit', isReplacementEvent: true });
    }
  }

  return flat.sort((a, b) => a.day - b.day);
}

export function deriveShipmentStatusFromDay(daysSinceOrder: number, template: WorkflowTemplate) {
  const flat = flattenWorkflowTemplate(template);
  const reached = flat.filter((x) => daysSinceOrder >= x.day);
  if (reached.length === 0) return 'Label Created' as const;
  const current = reached[reached.length - 1];

  if (current.phase === 'delivered') return 'Delivered' as const;
  if (current.phase === 'damaged') return 'Damaged' as const;
  if (current.phase === 'return') return 'Return Initiated' as const;
  if (current.phase === 'replacement_review' || current.phase === 'replacement_transit') return 'Replacement in Progress' as const;
  if (current.phase === 'out_for_delivery') return 'Out for Delivery' as const;
  if (current.phase === 'in_transit') return 'In Transit' as const;
  if (daysSinceOrder >= 1) return 'Collected' as const;
  return 'Label Created' as const;
}
